import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined'
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined'
import Box from '@mui/material/Box'
import { lightBlue } from '@mui/material/colors'
import { APIProvider, Map } from '@vis.gl/react-google-maps'
import { get } from 'lodash'
import PropTypes from 'prop-types'
import React, { useMemo, useState, useCallback, useRef } from 'react'

import { DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM, GOOGLE_MAPS_MAX_ZOOM, filterValidCoordPairs } from 'components/common/map/constants'
import ContainerInfoPopover from 'components/common/map/ContainerInfoPopover'
import MapControlsCluster from 'components/common/map/MapControlsCluster'
import { useContainerHoverInfo } from 'components/common/map/useContainerHoverInfo'
import { useRoutesDriverPathBreadcrumb } from 'components/common/map/useRoutesDriverPathBreadcrumb'
import { useRoutesDriverPathTimestamps } from 'components/common/map/useRoutesDriverPathTimestamps'
import AdaptiveContainerMarkerLayer from 'components/common/map/webgl/containers/AdaptiveContainerMarkerLayer'
import DriverPathOverlay from 'components/route-manager/common/map/DriverPathOverlay'
import { applyFeatureOffset } from 'components/route-manager/RouteManagerMapV2/utils/applyFeatureOffset'
import { calculateCollisionOffsets } from 'components/route-manager/RouteManagerMapV2/utils/calculateCollisionOffsets'
import { calculateContainerOffsets } from 'components/route-manager/RouteManagerMapV2/utils/calculateContainerOffsets'
import Constants from 'Constants'
import { useMapPreference } from 'hooks/useMapPreference'
import T from 'T'

import AdaptiveBulkMarkerLayer from './AdaptiveBulkMarkerLayer'
import BulkStopMarkerPopover from './BulkStopMarkerPopover'
import MapController from './MapController'
import MapSelectionHelpButton from './MapSelectionHelpButton'
import RoutePolyline from './RoutePolyline'
import useBulkMarkerPopover from './useBulkMarkerPopover'

const { GOOGLE_MAPS_API_KEY } = Constants

const DEFAULT_OFFSET = 4

// Stops + sequence are always available; containers / driver path appear only
// when the caller supplies that data (see useBulkTwoRouteMap), so the toggle
// group grows to match what the dialog can actually show.
const STOPS_ITEM = { value: 'stops', label: T.STOPS_LABEL, icon: <PlaceOutlinedIcon /> }
const SEQUENCE_ITEM = { value: 'sequence', label: T.SEQUENCE_LINE, icon: <RouteOutlinedIcon /> }
const CONTAINERS_ITEM = { value: 'containers', label: T.CONTAINERS, icon: <Inventory2OutlinedIcon /> }
const DRIVER_ITEM = { value: 'driver', label: T.DRIVER_PATH, icon: <TimelineOutlinedIcon /> }

const CommonSelectStopsToMoveMap = ({
  routes = [],
  fitBounds = false,
  onFitBoundsDone,
  fitCoordinates,
  onSelectMarkers,
  onDeselectMarkers,
  enableBoxSelect = false,
  mapRef,
  ToggleComponent,
  onMarkerClick,
  stopsById = {},
  controlsBottomOffset = 13,
  defaultShowSequenceLines = true,
  polylineProps = {},
  mapChildren = null,
  highlightedStopIds = null,
  containerFeatures = [],
  driverRouteIds = [],
  driverServiceDate = null,
}) => {
  // Persisted per-map (the 'bulk' namespace) so these controls stick across
  // reopen + session; driverPathDate stays local (a per-look reference).
  const [mapTypeId, setMapTypeId] = useMapPreference('bulk', 'basemap', 'roadmap')
  const [showStops, setShowStops] = useMapPreference('bulk', 'showStops', true)
  const [showSequenceLines, setShowSequenceLines] = useMapPreference('bulk', 'showSequence', defaultShowSequenceLines)
  const [showContainers, setShowContainers] = useMapPreference('bulk', 'showContainers', true)
  const [showDriverPath, setShowDriverPath] = useMapPreference('bulk', 'showDriver', false)
  const [driverPathDate, setDriverPathDate] = useState(null)
  const [offsetValue, setOffsetValue] = useMapPreference('bulk', 'offset', DEFAULT_OFFSET)
  const [containerOffsetValue, setContainerOffsetValue] = useMapPreference('bulk', 'containerOffset', DEFAULT_OFFSET)
  const [labelDensityLevel, setLabelDensityLevel] = useMapPreference('bulk', 'labelDensity', 0)
  const manualFitBoundsRef = useRef(null)

  const handleManualFitBounds = useCallback(fn => {
    manualFitBoundsRef.current = fn
  }, [])

  const handleFitBoundsClick = useCallback(() => {
    if (manualFitBoundsRef.current) manualFitBoundsRef.current()
  }, [])
  const { popoverStop, popoverPosition, handleMarkerMouseEnter, handleMarkerMouseLeave, handlePopoverMouseEnter, handlePopoverMouseLeave } =
    useBulkMarkerPopover({ stopsById })
  const { onHover: handleContainerHover, infoProps: containerInfoProps } = useContainerHoverInfo()

  const handleDeckHover = useCallback(
    (event, stopId) => {
      if (event && stopId) {
        handleMarkerMouseEnter(event, stopId)
      } else {
        handleMarkerMouseLeave()
      }
    },
    [handleMarkerMouseEnter, handleMarkerMouseLeave]
  )

  const [zoom, setZoom] = useState(DEFAULT_MAP_ZOOM)
  const handleBasemapToggle = useCallback(() => setMapTypeId(prev => (prev === 'satellite' ? 'roadmap' : 'satellite')), [setMapTypeId])
  const handleOffsetChange = useCallback(newVal => setOffsetValue(newVal), [setOffsetValue])
  const handleContainerOffsetChange = useCallback(newVal => setContainerOffsetValue(newVal), [setContainerOffsetValue])
  const handleCameraChanged = useCallback(ev => {
    const nextZoom = Math.round(ev.detail.zoom)
    setZoom(prev => (prev === nextZoom ? prev : nextZoom))
  }, [])

  const hasContainers = containerFeatures.length > 0
  const driverPathAvailable = driverRouteIds.length > 0
  const effectiveDriverDate = driverPathDate ?? driverServiceDate
  const handleDriverDateChange = useCallback(date => setDriverPathDate(date), [])

  // Driver path is a historical reference overlay — fetch the picked date's
  // breadcrumb (defaults to the dialog's service date) only while the layer is on.
  const breadcrumbsByRouteId = useRoutesDriverPathBreadcrumb({
    routeIds: driverRouteIds,
    date: effectiveDriverDate,
    enabled: showDriverPath,
  })
  const timestampsByRouteId = useRoutesDriverPathTimestamps({
    routeIds: driverRouteIds,
    date: effectiveDriverDate,
    enabled: showDriverPath,
  })

  const layerItems = useMemo(() => {
    const items = [STOPS_ITEM, SEQUENCE_ITEM]
    if (hasContainers) items.push(CONTAINERS_ITEM)
    if (driverPathAvailable) items.push(DRIVER_ITEM)
    return items
  }, [hasContainers, driverPathAvailable])

  const activeLayers = useMemo(() => {
    const next = []
    if (showStops) next.push('stops')
    if (showSequenceLines) next.push('sequence')
    if (hasContainers && showContainers) next.push('containers')
    if (driverPathAvailable && showDriverPath) next.push('driver')
    return next
  }, [showStops, showSequenceLines, hasContainers, showContainers, driverPathAvailable, showDriverPath])

  const handleLayersChange = useCallback(
    next => {
      setShowStops(next.includes('stops'))
      setShowSequenceLines(next.includes('sequence'))
      setShowContainers(next.includes('containers'))
      setShowDriverPath(next.includes('driver'))
    },
    [setShowStops, setShowSequenceLines, setShowContainers, setShowDriverPath]
  )

  const visibleRoutes = useMemo(() => routes.filter(r => r.visible !== false), [routes])
  const allMarkers = useMemo(() => routes.flatMap(r => r.markers || []), [routes])
  const visibleMarkers = useMemo(() => visibleRoutes.flatMap(r => r.markers || []), [visibleRoutes])

  // Stops spiderfy among co-located stops only. Containers ring out around their
  // stop on a separate slider (calculateContainerOffsets) so they're visible
  // beside the pin without dragging the stop off its true position.
  const offsetMap = useMemo(() => calculateCollisionOffsets(allMarkers, { zoom, baseOffset: offsetValue }), [allMarkers, zoom, offsetValue])

  const visibleOffsetMarkers = useMemo(
    () => (offsetValue > 0 ? visibleMarkers.map(f => applyFeatureOffset(f, offsetMap)) : visibleMarkers),
    [visibleMarkers, offsetMap, offsetValue]
  )

  const containerOffsetMap = useMemo(
    () => calculateContainerOffsets(containerFeatures, containerOffsetValue, { zoom }),
    [containerFeatures, containerOffsetValue, zoom]
  )

  const visibleOffsetContainerFeatures = useMemo(
    () => (containerOffsetValue > 0 ? containerFeatures.map(f => applyFeatureOffset(f, containerOffsetMap)) : containerFeatures),
    [containerFeatures, containerOffsetMap, containerOffsetValue]
  )

  const allCoordinates = useMemo(() => {
    if (fitCoordinates?.length > 0) return fitCoordinates
    return filterValidCoordPairs(visibleMarkers.map(m => get(m, 'geometry.coordinates')))
  }, [fitCoordinates, visibleMarkers])

  return (
    <Box height="100%" position="relative" width="100%">
      <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
        <Map
          disableDefaultUI
          clickableIcons={false}
          defaultCenter={DEFAULT_MAP_CENTER}
          defaultZoom={DEFAULT_MAP_ZOOM}
          gestureHandling="greedy"
          mapId="bulk-action-map"
          mapTypeId={mapTypeId}
          maxZoom={GOOGLE_MAPS_MAX_ZOOM}
          style={{ width: '100%', height: '100%' }}
          onCameraChanged={handleCameraChanged}
        >
          <MapControlsCluster
            showLabelSpacing
            activeLayers={activeLayers}
            bottomMargin={controlsBottomOffset}
            containerOffsetValue={containerOffsetValue}
            driverDateValue={effectiveDriverDate}
            fitBoundsBelowToggle={Boolean(ToggleComponent)}
            isSatellite={mapTypeId === 'satellite'}
            labelSpacingValue={labelDensityLevel}
            layerItems={layerItems}
            offsetValue={offsetValue}
            showContainerOffset={hasContainers}
            showDriverDate={showDriverPath}
            onBasemapToggle={handleBasemapToggle}
            onContainerOffsetChange={handleContainerOffsetChange}
            onDriverDateChange={handleDriverDateChange}
            onFitBounds={handleFitBoundsClick}
            onLabelSpacingChange={setLabelDensityLevel}
            onLayersChange={handleLayersChange}
            onOffsetChange={handleOffsetChange}
          />
          <MapController
            allCoordinates={allCoordinates}
            enableBoxSelect={enableBoxSelect}
            fitBounds={fitBounds}
            mapRef={mapRef}
            markers={visibleOffsetMarkers}
            onDeselectMarkers={onDeselectMarkers}
            onFitBoundsDone={onFitBoundsDone}
            onManualFitBounds={handleManualFitBounds}
            onSelectMarkers={onSelectMarkers}
          />
          {/* Keyed off the breadcrumbs, not `routes`: single-route callers don't always list the route there. */}
          {showDriverPath &&
            breadcrumbsByRouteId &&
            Object.entries(breadcrumbsByRouteId).map(([id, segments]) => {
              const route = routes.find(r => r.id === id)
              if (route?.visible === false) return null
              return (
                <DriverPathOverlay
                  key={`driver-${id}`}
                  forceDom
                  breadcrumb={segments}
                  routeColor={route?.color}
                  routeId={id}
                  routeLocationTimestamps={timestampsByRouteId?.[id]}
                />
              )
            })}
          {visibleRoutes.map(route => {
            const sequenceCoords = get(route, 'sequenceLine.geometry.coordinates', [])
            return showSequenceLines && sequenceCoords.length >= 2 ? (
              <RoutePolyline
                key={route.id}
                dashed
                coordinates={sequenceCoords}
                strokeColor={route.color || lightBlue[300]}
                strokeOpacity={0.8}
                strokeWeight={2}
                zIndex={1}
                {...polylineProps}
              />
            ) : null
          })}
          {showStops && (
            <AdaptiveBulkMarkerLayer
              features={visibleOffsetMarkers}
              highlightedStopIds={highlightedStopIds}
              labelDensityLevel={labelDensityLevel}
              totalStopCount={allMarkers.length}
              onClick={onMarkerClick}
              onHover={handleDeckHover}
              onMouseEnter={handleMarkerMouseEnter}
              onMouseLeave={handleMarkerMouseLeave}
            />
          )}
          {hasContainers && showContainers && (
            <AdaptiveContainerMarkerLayer features={visibleOffsetContainerFeatures} onHover={handleContainerHover} />
          )}
          <BulkStopMarkerPopover
            position={popoverPosition}
            stop={popoverStop}
            onMouseEnter={handlePopoverMouseEnter}
            onMouseLeave={handlePopoverMouseLeave}
          />
          <ContainerInfoPopover {...containerInfoProps} />
          {mapChildren}
        </Map>
        {ToggleComponent}
        {enableBoxSelect && <MapSelectionHelpButton />}
      </APIProvider>
    </Box>
  )
}

CommonSelectStopsToMoveMap.propTypes = {
  routes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      markers: PropTypes.array,
      sequenceLine: PropTypes.object,
      visible: PropTypes.bool,
      color: PropTypes.string,
    })
  ),
  fitBounds: PropTypes.bool,
  onFitBoundsDone: PropTypes.func,
  fitCoordinates: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
  onSelectMarkers: PropTypes.func,
  onDeselectMarkers: PropTypes.func,
  enableBoxSelect: PropTypes.bool,
  mapRef: PropTypes.shape({ current: PropTypes.object }),
  ToggleComponent: PropTypes.node,
  onMarkerClick: PropTypes.func,
  stopsById: PropTypes.object,
  controlsBottomOffset: PropTypes.number,
  defaultShowSequenceLines: PropTypes.bool,
  polylineProps: PropTypes.object,
  mapChildren: PropTypes.node,
  highlightedStopIds: PropTypes.instanceOf(Map),
  containerFeatures: PropTypes.array,
  driverRouteIds: PropTypes.arrayOf(PropTypes.string),
  driverServiceDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.oneOf([null])]),
}

export default CommonSelectStopsToMoveMap
